import { Injectable } from '@angular/core';
import f from 'odata-filter-builder';
import buildQuery from 'odata-query';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { JobTitleDocumentModel } from 'services/models/hiring/jobTitleDocument.model';
import { JobTitleHistoryModel } from 'services/models/hiring/jobTitleHistory.model';
import type { OptionModel } from 'services/models/model';
import { QueryParams } from 'services/models/odata/queryParams';
import { Response } from 'services/models/response';
import { WorkforcePlanJobTitleGroupModel } from 'services/models/workforceplan/workforceplan-shift.model';
import { environment } from '../../../environments/environment';
import { HttpService } from '../http.service';
import { JobTitleModel } from '../models/hiring/jobTitle.model';
import { ServiceBase } from '../servicebase';

@Injectable()
export class JobTitleService extends ServiceBase<JobTitleModel> {
  constructor(http: HttpService) {
    super('jobtitle', http, environment.apiUrl);
  }

  createEntity(input: any): JobTitleModel {
    return JobTitleModel.create(input);
  }

  loadJobTitlesByTitle(
    companyId: string,
    char: string,
    disabledLoading = false
  ): Observable<JobTitleModel[]> {
    const search = char || '';
    const filter = f()
      .and(f().eq('companyId', companyId).toString().replace(/'/g, ''))
      .and(f().contains('title', search).or(f().contains('code', search)))
      .toString();

    const params = {
      select: [
        'title',
        'description',
        'code',
        'salary',
        'companyId',
        'companyGroupId',
        'salaryScaleId',
      ],
      orderBy: 'title',
      orderByOrder: 'asc',
      filter: buildQuery({ filter }).replace('?', '&'),
    } as QueryParams;

    return this.list(params, disabledLoading).pipe(map(res => res.data.filter(x => x.enable)));
  }

  loadJobTitlesBySearch(term: string, disabledLoading = true): Observable<JobTitleModel[]> {
    const search = (term || '').trim().toLowerCase();
    const filterStr =
      search.length > 0
        ? f()
            .and(f().eq('enable', true))
            .and(
              f()
                .contains((x: any) => x.toLower('title'), search)
                .or(f().contains((x: any) => x.toLower('code'), search))
            )
            .toString()
        : f().eq('enable', true).toString();

    const params = {
      select: ['id', 'title', 'code', 'companyName'],
      orderBy: 'title',
      orderByOrder: 'asc',
      top: 50,
      filter: buildQuery({ filter: filterStr }).replace('?', '&'),
    } as QueryParams;

    return this.list(params, disabledLoading).pipe(map(res => res.data.filter(x => x.enable)));
  }

  loadJobTitlesBySearchAndCompanyIds(
    term: string,
    companyIds: string[],
    disabledLoading = true
  ): Observable<JobTitleModel[]> {
    const validCompanyIds = (companyIds || []).filter(id => !!id);
    if (validCompanyIds.length === 0) {
      return this.loadJobTitlesBySearch(term, disabledLoading);
    }

    const normalizedTerm = (term || '').trim().toLowerCase().replace(/'/g, "''");
    const companyFilter = validCompanyIds.map(id => `companyId eq ${id}`).join(' or ');
    const searchFilter =
      normalizedTerm.length > 0
        ? ` and (contains(tolower(title), '${normalizedTerm}') or contains(tolower(code), '${normalizedTerm}'))`
        : '';

    const params = {
      select: ['id', 'title', 'code', 'companyName', 'companyId'],
      orderBy: 'title',
      orderByOrder: 'asc',
      top: 50,
      filter: `&$filter=enable eq true and (${companyFilter})${searchFilter}`,
    } as QueryParams;

    return this.list(params, disabledLoading).pipe(map(res => res.data.filter(x => x.enable)));
  }

  loadJobTitlesByCompanyGroup(companyGroupId: string, char: string): Observable<JobTitleModel[]> {
    const search = char || '';
    const filter = f()
      .and(f().eq('companyGroupId', companyGroupId).toString().replace(/'/g, ''))
      .and(f().contains('title', search).or(f().contains('code', search)))
      .toString();

    const params = {
      select: [
        'title',
        'description',
        'code',
        'salary',
        'companyId',
        'companyName',
        'companyGroupId',
        'salaryScaleId',
      ],
      orderBy: 'title',
      orderByOrder: 'asc',
      filter: buildQuery({ filter }).replace('?', '&'),
    } as QueryParams;

    return this.list(params).pipe(map(res => res.data.filter(x => x.enable)));
  }

  getFormDocuments(jobtitleId: string): Observable<Response<JobTitleDocumentModel[]>> {
    return this.http.get(`${this.endpoint}/${jobtitleId}/form-documents`).pipe(
      map(res => {
        const response = new Response<JobTitleDocumentModel[]>();
        response.data = res.body.map(
          pp => JobTitleDocumentModel.create(pp) as JobTitleDocumentModel
        );
        response.count = res.headers.get('x-count');
        return response;
      })
    );
  }

  loadJobTitlesNotIn(
    jobTitlesId: string[],
    params: QueryParams,
    disableLoading: boolean = false
  ): Observable<Response<JobTitleModel[]>> {
    const query = this.createBuildQuery(params);
    return this.http
      .post(
        `/${this.endpoint}/load-jobtitles-excluding-listeds${query}`,
        jobTitlesId,
        undefined,
        this.baseUrl,
        disableLoading
      )
      .pipe(
        map(res => {
          const response = new Response<JobTitleModel[]>();
          response.data = res.map(pp => JobTitleModel.create(pp) as JobTitleModel);
          response.count = res.length;
          return response;
        })
      );
  }

  getJobTitlesByTitleAndGroup(
    companyId: string,
    jobTitleGroupId: string,
    char: string = '',
    disabledLoading = true
  ): Observable<JobTitleModel[]> {
    const search = char || '';
    let searchFilter = '';

    if (search.length > 0)
    {
      searchFilter = f().contains('title', search).or(f().contains('code', search)).toString();
    }
    const filter = f()
        .and(f().eq('companyId', companyId).toString().replace(/'/g, ''))
        .and(f().eq('jobTitleGroupId', jobTitleGroupId).toString().replace(/'/g, ''))
        .and(searchFilter)
        .toString();

    const params = {
      select: [
        'title',
        'description',
        'code',
        'salary',
        'companyId',
        'companyGroupId',
        'salaryScaleId',
        'jobTitleGroupId',
        'jobTitleSeniorizationId',
      ],
      orderBy: 'title',
      orderByOrder: 'asc',
      filter: buildQuery({ filter }).replace('?', '&'),
    } as QueryParams;

    return this.list(params, disabledLoading).pipe(map(res => res.data.filter(x => x.enable)));
  }

  getJobTitlesByCompany(companyId: string): Observable<JobTitleModel[]> {
    const filter = f().and(f().eq('companyId', companyId).toString().replace(/'/g, '')).toString();

    const params = {
      select: [
        'id',
        'title',
        'description',
        'code',
        'salary',
        'companyId',
        'companyName',
        'salaryScaleId',
      ],
      orderBy: 'title',
      orderByOrder: 'asc',
      filter: buildQuery({ filter }).replace('?', '&'),
    } as QueryParams;

    return this.list(params, true).pipe(map(res => res.data.filter(x => x.enable)));
  }

  listFromEffectiveWorkforcePlan(companyId: string, costCenterId: string) {
    const params = {
      select: ['id', 'title', 'code', 'jobTitleGroupId'],
      orderBy: 'title',
      orderByOrder: 'asc',
    } as QueryParams;

    return this.http.get(
      `${this.endpoint}/list-from-effective-workforce-plan?costCenterId=${costCenterId}&companyId=${companyId}`,
      { params }
    );
  }

  getListFiltered(params?: any): Observable<JobTitleModel[]> {
    const queryParams = `?${params.join('&')}`;
    return this.http
      .get(`${this.endpoint}/odata${queryParams}`, null, null, null, true)
      .pipe(map((data: any) => data.body.map(item => JobTitleModel.create(item))));
  }

  listByGroupIds(jobTitleGroupIds: string[], companyIds?: string[]): Observable<JobTitleModel[]> {
    if (!jobTitleGroupIds || jobTitleGroupIds.length === 0) {
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }

    const jobTitleGroupFilter = jobTitleGroupIds.map(id => `jobTitleGroupId eq ${id}`).join(' or ');
    let finalFilter = `(${jobTitleGroupFilter}) and enable eq true`;

    if (companyIds && companyIds.length > 0) {
      const companyIdFilter = companyIds.map(id => `companyId eq ${id}`).join(' or ');
      finalFilter = `(${jobTitleGroupFilter}) and (${companyIdFilter}) and enable eq true`;
    }

    const queryParams = [
      `$filter=${encodeURIComponent(finalFilter)}`,
      `$select=id,title,code,companyName`,
      `$orderby=title asc`,
    ];

    return this.http
      .get(`${this.endpoint}/odata?${queryParams.join('&')}`, null, null, null, true)
      .pipe(map((data: any) => data.body.map(item => JobTitleModel.create(item))));
  }

  listHistoryByEmployee(employeeId: string, disabledLoading = true): Observable<JobTitleHistoryModel[]> {
    if (!employeeId) {
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }

    return this.http
      .get(`${this.endpoint}/history/employee/${employeeId}`, { observe: 'response' }, undefined, undefined, disabledLoading)
      .pipe(
        map((res: any) => {
          const body = res?.body ?? res;
          if (!Array.isArray(body)) {
            return [];
          }
          return body.map((item: any) => JobTitleHistoryModel.create(item));
        })
      );
  }
}
